import React, { useEffect, useState } from 'react';
import { useLang } from '../../context/LanguageContext';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { adminListFreezones, adminUpdateFreezone } from '../../api';
import { toast } from '../../hooks/use-toast';
import { Save, Star } from 'lucide-react';

const AdminFreezones = () => {
  const { lang } = useLang();
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListFreezones();
      setZones(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (code, field, value) => {
    setEdits((e) => ({ ...e, [code]: { ...(e[code] || {}), [field]: value } }));
  };

  const save = async (zone) => {
    const changes = edits[zone.code];
    if (!changes || Object.keys(changes).length === 0) return;
    setSaving(zone.code);
    try {
      await adminUpdateFreezone(zone.code, changes);
      toast({ title: lang === 'ar' ? 'تم الحفظ' : 'Saved' });
      setEdits((e) => { const c = { ...e }; delete c[zone.code]; return c; });
      load();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
    finally { setSaving(null); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl md:text-4xl text-[#0a0a0a]">{lang === 'ar' ? 'المناطق الحرة' : 'Free Zones'}</h1>
        <p className="text-sm text-[#111827]/60 mt-2">{lang === 'ar' ? 'تعديل الأسعار والتوفر' : 'Edit prices and availability'}</p>
      </div>

      {loading ? <div className="glass-card rounded-2xl p-10 text-center text-[#111827]/60">…</div>
      : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {zones.map((z) => {
            const changed = !!edits[z.code];
            const currentCost = edits[z.code]?.min_cost ?? z.min_cost;
            const currentVisas = edits[z.code]?.visas ?? z.visas;
            const currentPopular = edits[z.code]?.popular ?? z.popular;
            return (
              <div key={z.code} className="glass-card rounded-2xl p-5">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <div className="font-display text-xl text-[#0a0a0a]">{z.name?.en}</div>
                    <div className="text-xs text-[#2563eb] mt-0.5">{z.category?.en}</div>
                    <div className="text-[10px] text-[#111827]/40 mt-1">{z.code}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={currentPopular} onCheckedChange={(v) => set(z.code, 'popular', v)} className="data-[state=checked]:bg-[#2563eb]" />
                    {currentPopular && <Star className="w-4 h-4 text-[#3b82f6] fill-[#3b82f6]" />}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#2563eb]/70 block mb-1">{lang === 'ar' ? 'الحد الأدنى (AED)' : 'Min Cost (AED)'}</label>
                    <Input type="number" value={currentCost} onChange={(e) => set(z.code, 'min_cost', parseInt(e.target.value, 10) || 0)} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#2563eb]/70 block mb-1">{lang === 'ar' ? 'تأشيرات' : 'Visas'}</label>
                    <Input type="number" value={currentVisas} onChange={(e) => set(z.code, 'visas', parseInt(e.target.value, 10) || 0)} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" />
                  </div>
                </div>
                <Button disabled={!changed || saving === z.code} onClick={() => save(z)} className="gold-button w-full rounded-md h-9 text-xs mt-4">
                  <Save className="w-3.5 h-3.5 me-2" />{saving === z.code ? '…' : (lang === 'ar' ? 'حفظ التغييرات' : 'Save Changes')}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminFreezones;
